/**
 * opentokSubscriber.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";

import * as React from "react";
import OT from "@opentok/client";

import IconButton from "@mui/material/IconButton";
//import Tooltip from "@mui/material/Tooltip";

import { SubscriberStream } from "../models/stream";

interface Props {
  session: OT.Session,
  stream: SubscriberStream,
  streamWidth: number | undefined,
  streamHeight: number | undefined,
  hideVideo?: boolean,
  className?: string,
  style?: React.CSSProperties,
  isPinned?: boolean | null,
  onSpeakStart?: (stream: SubscriberStream) => void,
  onSpeakEnd?: (stream: SubscriberStream) => void,
  onClick?: (stream: SubscriberStream) => void
}

interface State {
  speaking: boolean,
  isMuted: boolean,
  isRemoteMuted: boolean,
  streamingVideo: boolean,
  streamName: string,
  subscriber: OT.Subscriber | null,
  volume: number
}

export class OTStreamSubscriber extends React.Component<Props, State> {

  private lastTimestamp: number | null;

  constructor(props: Props) {
    super(props);
    this.lastTimestamp = null;

    this.onAudioLevelChange = this.onAudioLevelChange.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
    this.toggleVideo = this.toggleVideo.bind(this);

    this.state = {
      speaking: false,
      isMuted: false,
      isRemoteMuted: false,
      streamingVideo: !props.hideVideo,
      streamName: "",
      subscriber: null,
      volume: 100
    };
  }

  async componentDidMount (): Promise<void> {
    try {
      const subscriber = await this.createSubscriber();
      this.setState({
        subscriber: subscriber,
        streamName: this.props.stream.name,
        volume: subscriber.getAudioVolume()
      });
    } catch (err) {
      console.error("Error making subscriber:", err);
    }
  }

  componentDidUpdate (prevProps: Props): void {
    if (this.props.hideVideo && !prevProps.hideVideo) {
      this.state.subscriber?.subscribeToVideo(false);
      this.setState({streamingVideo: false});
    } else if (!this.props.hideVideo && prevProps.hideVideo) {
      this.state.subscriber?.subscribeToVideo(true);
      this.setState({streamingVideo: true});
    }
    if (this.props.streamWidth && this.props.streamHeight && this.props.streamWidth !== prevProps.streamWidth) {
      this.state.subscriber?.setPreferredResolution({
        width: this.props.streamWidth,
        height: this.props.streamHeight
      });
    }
  }

  componentWillUnmount(): void {
    this.destroySubscriber();
  }

  async createSubscriber (): Promise<OT.Subscriber> {
    const options: OT.SubscriberProperties = {
      insertMode: "append",
      width: "100%",
      height: "100%",
      showControls: false,
      subscribeToVideo: !this.props.hideVideo
    };

    const subscriber = await new bluebird<OT.Subscriber>((resolve, reject) => {
      if (!this.props.stream.stream) {
        return reject(Error("Subscribe called on fake stream"));
      }
      const subscriber = this.props.session.subscribe(
          this.props.stream.stream, this.props.stream.nodeID, options, error => {
        if (error) { return reject(error); }
        resolve(subscriber);
      });
    });

    subscriber.on("audioLevelUpdated", this.onAudioLevelChange);

    return subscriber;
  }

  destroySubscriber(): void {
    if (!this.state.subscriber) { return; }

    this.state.subscriber.off("audioLevelUpdated", this.onAudioLevelChange);

    this.state.subscriber.once("destroyed", () => {
      console.log("subscriber destroyed");
    });
    this.props.session.unsubscribe(this.state.subscriber);
  }

  onAudioLevelChange(event: OT.Event<"audioLevelUpdated", OT.Subscriber> & {audioLevel: number}): void {
    /*if (event.audioLevel > 0) {
      console.log("audio level:", event.audioLevel);
    }*/
    const now = Date.now();
    const update: any = {isRemoteMuted: event.audioLevel === null};

    if (event.audioLevel > 0.08) {
      if (this.lastTimestamp === null) {
        this.lastTimestamp = now;
        update.speaking = false;
      } else if (this.state.speaking) {
        this.lastTimestamp = now;
      } else if (now - this.lastTimestamp > 800) {
        update.speaking = true;
        this.props.stream.speaking = true;
        this.props.onSpeakStart && this.props.onSpeakStart(this.props.stream);
      }
      this.setState(update);
    } else if (this.lastTimestamp !== null && now - this.lastTimestamp > 1000) {
      if (this.state.speaking) {
        update.speaking = false;
        this.props.stream.speaking = false;
        this.props.onSpeakEnd && this.props.onSpeakEnd(this.props.stream);
      }
      this.setState(update);
    }
  }

  toggleMute() {
    if (this.state.isMuted) {
      this.state.subscriber?.subscribeToAudio(true);
    } else {
      this.state.subscriber?.subscribeToAudio(false);
    }
    this.setState({isMuted: !this.state.isMuted});
  }

  toggleVideo() {
    if (this.props.hideVideo) {
      if (this.state.streamingVideo) {
        this.setState({streamingVideo: false});
        this.state.subscriber?.subscribeToVideo(false);
      }
      return;
    }
    if (this.state.streamingVideo) {
      this.state.subscriber?.subscribeToVideo(false);
    } else {
      this.state.subscriber?.subscribeToVideo(true);
    }
    this.setState({streamingVideo: !this.state.streamingVideo});
  }

  render(): React.ReactElement<any, any> {
    const cssName = (this.props.className || "") +
      (this.state.speaking ? " speaking" : "") +
      (this.state.isRemoteMuted ? " muted": "");

    return <div className={cssName} style={this.props.style}>
      <div className="upper-controls">
        <div className="left-side">
          <span className="stream-name">{this.state.streamName}</span>
        </div>
        <div className="right-side">
        <IconButton size="small" className="video-btn"
            onClick={this.toggleVideo}
              disableRipple disableTouchRipple>
            <i className={"fa-solid " + (this.state.streamingVideo ? "fa-video" : "fa-video-slash")}></i>
          </IconButton>
          <IconButton size="small" onClick={this.toggleMute} className="volume-btn"
              disableRipple disableTouchRipple>
            <i className={"fa-solid " + (this.state.isMuted ? "fa-volume-xmark" : "fa-volume-high")}></i>
          </IconButton>
        </div>
      </div>
      <div className="subscriber-inner" id={this.props.stream.nodeID}
        data-stream-id={this.props.stream.streamId}
        onClick={() => this.props.onClick && this.props.onClick(this.props.stream)} />
      <div className="lower-controls">
        <i className={"fa-solid muted-indicator " + (this.state.isMuted ? "fa-microphone-slash" : "")} />
        <i className={"fas pinned-indicator " + (this.props.isPinned ? "fa-thumbtack" : "")} />
      </div>
    </div>;
  }
}
